// Ink's external functions bound to the story (decision 0054: "dialogue in ink, wired to the shared condition language
// and story state"): ink reads facts and conditions through the story's `Conditions` and writes through the story's
// actions, so a line, a quest, a shop and a bark all see the same state. Items and game actions go to the game.
import type { FactValue, Story, StoryAction } from "@rpg/story";
import type { InkBindings } from "./ink";

const STATES = ["offered", "active", "done", "failed"] as const;

/** Bind ink to a story. `npc()`: who is being talked to (gifts and memory go to them). `host`: the game's actions
 *  (`act`, `give`, `take` arrive as `{ host: name, args }`; `take` answers whether the player had the items). */
export function storyBindings(story: Story, o: { npc(): string | null; host(name: string, args: Record<string, FactValue>): boolean | void }): InkBindings {
  const npc = (what: string): string => {
    const n = o.npc();
    if (!n) throw new Error(`dialogue: ${what} with no one to talk to`);
    return n;
  };
  const apply = (a: StoryAction): void => story.apply([a], { kind: "dialogue" });
  return {
    fact: (name) => story.conditions.fact(name),
    cond: (expr) => story.conditions.test(expr),
    set: (name, value) => apply({ set: name, value }),
    add: (name, amount) => apply({ add: name, amount }),
    quest(id, state) {
      if (!(STATES as readonly string[]).includes(state)) throw new Error(`dialogue: quest("${id}", "${state}"): not a quest state (${STATES.join(", ")})`);
      apply({ quest: id, state: state as (typeof STATES)[number] });
    },
    affinity: (who, amount) => apply({ affinity: who, amount }),
    standing: (faction, amount) => apply({ standing: faction, amount }),
    gift: (item) => story.gift(npc("a gift"), item),
    remember: (key, value) => apply({ remember: npc("a memory"), key, value }),
    act: (name, arg) => void o.host(name, { arg }),
    give: (item, count) => void o.host("give", { item, count }),
    take: (item, count) => o.host("take", { item, count }) === true,
  };
}
